import { useEffect, useState } from 'react';
import { EpubRendererType } from '../../../types/manageBooks';
import { useTranslation } from '../../../i18n/useTranslation';

interface Props {
  open: boolean;
  bookTitle: string;
  currentRenderer: EpubRendererType;
  isSubmitting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

function targetOf(current: EpubRendererType): EpubRendererType {
  return current === EpubRendererType.CustomHtml
    ? EpubRendererType.EpubJs
    : EpubRendererType.CustomHtml;
}

/**
 * Two-step confirmation modal: consequences list + mandatory acknowledgment
 * checkbox. Confirm button stays disabled until the checkbox is ticked, which
 * forces admins to read the consequences before taking an irreversible action.
 *
 * UX safety: Escape and outside-click both cancel. Enter key is deliberately
 * a no-op so a stray keystroke can't confirm.
 */
export function SwitchRendererConfirmDialog({
  open,
  bookTitle,
  currentRenderer,
  isSubmitting,
  onConfirm,
  onCancel,
}: Props) {
  const { t } = useTranslation();
  const [acknowledged, setAcknowledged] = useState(false);
  const target = targetOf(currentRenderer);

  const rendererLabel = (r: EpubRendererType): string =>
    r === EpubRendererType.EpubJs
      ? t('App.Common.RendererType.EpubJs')
      : t('App.Common.RendererType.CustomHtml');

  // Reset the checkbox every time the dialog opens, so a stale "acknowledged"
  // state from a previous book can't auto-enable confirm.
  useEffect(() => {
    if (open) setAcknowledged(false);
  }, [open]);

  // Escape closes (any time, even while submitting — users can bail out; the
  // in-flight mutation still completes but the dialog hides).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
      // Enter deliberately does nothing.
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      role="presentation"
      onClick={onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="switch-renderer-dialog-title"
        className="bg-white rounded-lg shadow-2xl max-w-lg w-full"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-neutral-200">
          <h2 id="switch-renderer-dialog-title" className="text-lg font-semibold text-neutral-900">
            {t('App.Admin.Books.SwitchRendererConfirmDialog.Title', { from: rendererLabel(currentRenderer), to: rendererLabel(target) })}
          </h2>
          <p className="mt-1 text-sm text-neutral-600">
            {t('App.Admin.Books.SwitchRendererConfirmDialog.BookSubtitle', { title: bookTitle })}
          </p>
        </div>

        <div className="px-5 py-4 space-y-3 text-sm text-neutral-700">
          <ul className="list-disc pl-5 space-y-1">
            <li>{t('App.Admin.Books.SwitchRendererConfirmDialog.Consequence1')}</li>
            <li>{t('App.Admin.Books.SwitchRendererConfirmDialog.Consequence2')}</li>
            <li>{t('App.Admin.Books.SwitchRendererConfirmDialog.Consequence3')}</li>
            <li>{t('App.Admin.Books.SwitchRendererConfirmDialog.Consequence4')}</li>
          </ul>

          <label className="flex items-start gap-2 pt-2 border-t border-neutral-200">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={e => setAcknowledged(e.target.checked)}
              className="mt-0.5 w-4 h-4"
              aria-describedby="switch-renderer-ack-text"
            />
            <span id="switch-renderer-ack-text" className="text-sm text-neutral-800">
              {t('App.Admin.Books.SwitchRendererConfirmDialog.AcknowledgeText')}
            </span>
          </label>
        </div>

        <div className="px-5 py-3 bg-neutral-50 border-t border-neutral-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm font-medium text-neutral-700 border border-neutral-300 rounded-md hover:bg-neutral-100 min-h-[40px]"
          >
            {t('App.Admin.Books.SwitchRendererConfirmDialog.Cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!acknowledged || isSubmitting}
            className="px-3 py-1.5 text-sm font-semibold text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed min-h-[40px]"
          >
            {isSubmitting ? t('App.Admin.Books.SwitchRendererConfirmDialog.Switching') : t('App.Admin.Books.SwitchRendererConfirmDialog.Confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}
